import { Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { UserSelectorAvatar } from '@/shared/components/user-selector/UserSelectorAvatar';
import type { SignalRelatedIssue } from '@momentum/types';
import { IssueType } from '@momentum/types';
import type { EmployeeInfo } from '../../constants';
import { STATUS_CONFIG } from './signal-detail.constants';

interface SignalLinkedIssuesProps {
  relatedIssues: SignalRelatedIssue[] | undefined;
  employeeMap: Map<string, EmployeeInfo>;
}

export function SignalLinkedIssues({
  relatedIssues,
  employeeMap,
}: SignalLinkedIssuesProps) {
  const { t } = useTranslation('knowledge');

  return (
    <div className="border-t pt-6">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[13px] font-semibold text-muted-foreground">
          <Zap className="h-3.5 w-3.5" />
          {t('detailLinkedIssues')}
        </div>
        {relatedIssues && relatedIssues.length > 0 && (
          <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
            {relatedIssues.length}
          </span>
        )}
      </div>
      {relatedIssues && relatedIssues.length > 0 ? (
        <div className="flex flex-col gap-0.5">
          {relatedIssues.map((issue: SignalRelatedIssue) => {
            const fallback = {
              labelKey: 'statusTodo',
              color: '#64748b',
              bg: '#f1f5f9',
            };
            const cfg = STATUS_CONFIG[issue.status] ?? fallback;

            return (
              <div
                key={issue._id}
                className="flex items-center gap-2.5 rounded-md px-2 py-2 text-[13px] text-foreground transition-colors hover:bg-muted/50"
              >
                <span
                  className="flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium leading-none"
                  style={{ color: cfg.color, backgroundColor: cfg.bg }}
                >
                  {t(cfg.labelKey as never)}
                </span>
                <span className="truncate flex-1">{issue.title}</span>
                <span className="flex-shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                  {issue.type === IssueType.GOAL
                    ? t('issueTypeGoal')
                    : t('issueTypeTask')}
                </span>
                <div className="flex flex-shrink-0 -space-x-1.5 rtl:space-x-reverse">
                  {issue.assignees.slice(0, 3).map((userId) => {
                    const emp = employeeMap.get(userId);
                    return emp ? (
                      <UserSelectorAvatar
                        key={userId}
                        fullName={emp.fullName}
                        imageUrl={emp.imageUrl}
                        size="xs"
                        variant="neutral"
                      />
                    ) : null;
                  })}
                  {issue.assignees.length > 3 && (
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[8px] font-medium text-muted-foreground ring-2 ring-white">
                      +{issue.assignees.length - 3}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-[13px] text-muted-foreground">
          {t('detailNoLinkedIssues')}
        </p>
      )}
    </div>
  );
}
